import { ref } from 'vue';
import { defineStore } from 'pinia';
import { secure, StorageKeys } from '@/core/storage/secure';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/app/i18n';
import { dateConfig, type DateFormat, type TimeFormat, type WeekStart } from '@/shared/utils/date';
import { numberConfig, type NumberFormat, type CurrencyPosition } from '@/shared/utils/money';

export type ThemeMode = 'system' | 'light' | 'dark';
export type Locale = SupportedLocale;

export const DATE_FORMATS = ['dmy', 'mdy', 'iso', 'long'] as const;
export const TIME_FORMATS = ['24h', '12h'] as const;
export const NUMBER_FORMATS = ['comma', 'dot'] as const;
export const CURRENCY_POSITIONS = ['left', 'right'] as const;
export const WEEK_STARTS = ['monday', 'sunday'] as const;

const oneOf = <T extends string>(list: readonly T[], v: string | null): v is T =>
  v != null && (list as readonly string[]).includes(v);

/** Accent presets — see `src/app/theme/tokens.scss` for the palettes.
 *  `'blue'` is the default; on <html> we drop the `data-accent`
 *  attribute so the base :root palette applies. */
export const ACCENTS = ['blue', 'purple', 'emerald', 'rose', 'amber', 'slate'] as const;
export type Accent = (typeof ACCENTS)[number];

function isSupportedLocale(v: string | null): v is Locale {
  return v != null && (SUPPORTED_LOCALES as readonly string[]).includes(v);
}

function isAccent(v: string | null): v is Accent {
  return v != null && (ACCENTS as readonly string[]).includes(v);
}

export const useAppStore = defineStore('app', () => {
  const theme = ref<ThemeMode>('system');
  const locale = ref<Locale>('sq');
  const accent = ref<Accent>('blue');
  // Display-format preferences. The refs are the reactive source of truth for
  // the settings UI; each setter also mirrors the value into the formatter
  // configs (dateConfig / numberConfig) so the whole app reflects the choice.
  const dateFormat = ref<DateFormat>('dmy');
  const timeFormat = ref<TimeFormat>('24h');
  const numberFormat = ref<NumberFormat>('comma');
  const currencyPosition = ref<CurrencyPosition>('left');
  const weekStart = ref<WeekStart>('monday');
  // POS browse pref: hide out-of-stock products from the catalog grid. On by
  // default for a clean grid; the operator can flip it per device. Search and
  // scan always bypass it so an out-of-stock item is never truly unreachable.
  const posHideOutOfStock = ref(true);

  /** Push the current display prefs into the module-level formatter configs. */
  function applyFormats(): void {
    dateConfig.dateFormat = dateFormat.value;
    dateConfig.timeFormat = timeFormat.value;
    dateConfig.weekStart = weekStart.value;
    dateConfig.locale = locale.value === 'sq' ? 'sq-AL' : `${locale.value}`;
    numberConfig.numberFormat = numberFormat.value;
    numberConfig.currencyPosition = currencyPosition.value;
  }

  async function hydrate(): Promise<void> {
    const [t, l, a, df, tf, nf, cp, ws, hoos] = await Promise.all([
      secure.getString(StorageKeys.AppTheme),
      secure.getString(StorageKeys.AppLocale),
      secure.getString(StorageKeys.AppAccent),
      secure.getString(StorageKeys.AppDateFormat),
      secure.getString(StorageKeys.AppTimeFormat),
      secure.getString(StorageKeys.AppNumberFormat),
      secure.getString(StorageKeys.AppCurrencyPosition),
      secure.getString(StorageKeys.AppWeekStart),
      secure.getString(StorageKeys.AppPosHideOutOfStock),
    ]);
    if (t === 'system' || t === 'light' || t === 'dark') theme.value = t;
    if (isSupportedLocale(l)) locale.value = l;
    if (isAccent(a)) accent.value = a;
    if (oneOf(DATE_FORMATS, df)) dateFormat.value = df;
    if (oneOf(TIME_FORMATS, tf)) timeFormat.value = tf;
    if (oneOf(NUMBER_FORMATS, nf)) numberFormat.value = nf;
    if (oneOf(CURRENCY_POSITIONS, cp)) currencyPosition.value = cp;
    if (oneOf(WEEK_STARTS, ws)) weekStart.value = ws;
    if (hoos === 'true' || hoos === 'false') posHideOutOfStock.value = hoos === 'true';
    applyAccent(accent.value);
    applyFormats();
  }

  async function setTheme(value: ThemeMode): Promise<void> {
    theme.value = value;
    await secure.setString(StorageKeys.AppTheme, value);
    applyThemeClass(value);
  }

  async function setLocale(value: Locale): Promise<void> {
    locale.value = value;
    await secure.setString(StorageKeys.AppLocale, value);
    applyFormats(); // 'long' date month names follow the language
  }

  async function setDateFormat(value: DateFormat): Promise<void> {
    dateFormat.value = value;
    await secure.setString(StorageKeys.AppDateFormat, value);
    applyFormats();
  }
  async function setTimeFormat(value: TimeFormat): Promise<void> {
    timeFormat.value = value;
    await secure.setString(StorageKeys.AppTimeFormat, value);
    applyFormats();
  }
  async function setNumberFormat(value: NumberFormat): Promise<void> {
    numberFormat.value = value;
    await secure.setString(StorageKeys.AppNumberFormat, value);
    applyFormats();
  }
  async function setCurrencyPosition(value: CurrencyPosition): Promise<void> {
    currencyPosition.value = value;
    await secure.setString(StorageKeys.AppCurrencyPosition, value);
    applyFormats();
  }
  async function setWeekStart(value: WeekStart): Promise<void> {
    weekStart.value = value;
    await secure.setString(StorageKeys.AppWeekStart, value);
    applyFormats();
  }

  async function setAccent(value: Accent): Promise<void> {
    accent.value = value;
    await secure.setString(StorageKeys.AppAccent, value);
    applyAccent(value);
  }

  async function setPosHideOutOfStock(value: boolean): Promise<void> {
    posHideOutOfStock.value = value;
    await secure.setString(StorageKeys.AppPosHideOutOfStock, value ? 'true' : 'false');
  }

  function applyThemeClass(value: ThemeMode = theme.value): void {
    const root = document.documentElement;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = value === 'dark' || (value === 'system' && prefersDark);
    root.classList.toggle('dark', shouldBeDark);
    // Belt-and-suspenders: strip any inline background / color-scheme an
    // older build may have written onto <html>. The class-gated CSS in
    // index.html now owns both, so lingering inline styles from a
    // previous session would keep the "washed-out" light theme stuck
    // until the operator toggled dark → light manually.
    if (root.style.background) root.style.background = '';
    if (root.style.colorScheme) root.style.colorScheme = '';
  }

  function applyAccent(value: Accent = accent.value): void {
    const root = document.documentElement;
    if (value === 'blue') {
      // The default palette lives on :root — clear the attribute so a
      // stale cascade from an earlier accent doesn't stick around.
      root.removeAttribute('data-accent');
    } else {
      root.setAttribute('data-accent', value);
    }
  }

  return {
    theme,
    locale,
    accent,
    dateFormat,
    timeFormat,
    numberFormat,
    currencyPosition,
    weekStart,
    posHideOutOfStock,
    hydrate,
    setTheme,
    setLocale,
    setAccent,
    setPosHideOutOfStock,
    setDateFormat,
    setTimeFormat,
    setNumberFormat,
    setCurrencyPosition,
    setWeekStart,
    applyThemeClass,
    applyAccent,
  };
});
